import { isVisible } from '@testing-library/user-event/dist/utils';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { IHttpService, HttpService } from '../../../services/httpService';
import {
	INotifierService,
	NotifierService
} from '../../../services/notifierService';
import { IAddUserProps } from './IAddUserProps';
import styles from './AddUser.module.scss';
import { ProgressSpinner } from 'primereact/progressspinner';
import {
	IUserDivisionsUpdateRequestModel,
	UserRequestModel
} from '../../../models/users/UserRequestModel';
import { MultiSelect, MultiSelectChangeParams } from 'primereact/multiselect';
import { DropdownOption } from '../../../models/common/DropdownOption';
import { Dropdown } from 'primereact/dropdown';
import { nameof } from '../../../shared/utility';
import { Checkbox } from 'primereact/checkbox';
import { IAddUserOptionsModel } from './models/addUserOptionsModel';

const AddUser = ({ setShouldReload }: IAddUserProps): JSX.Element => {
	const [userData, setUserData] = useState<UserRequestModel>({
		firstName: '',
		lastName: '',
		userName: '',
		email: '',
		securityRoleId: undefined,
		defaultDivisionId: undefined,
		divisions: []
	});

	const {
		firstName,
		lastName,
		userName,
		email,
		securityRoleId,
		defaultDivisionId,
		divisions
	} = userData;

	const activeDivisionsIds = divisions.map((d) => d.divisionId);

	const [dropdownsOptions, setDropdownsOptions] =
		useState<IAddUserOptionsModel>({
			securityRolesOptions: [],
			divisionsOptions: []
		});

	const { securityRolesOptions, divisionsOptions } = dropdownsOptions;

	const [isVisible, setIsVisible] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const httpService: IHttpService = useMemo(() => new HttpService(), []);
	const notifierService: INotifierService = useMemo(
		() => new NotifierService(),
		[]
	);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const [securityRoles, divisions] = await Promise.all([
					httpService.get<DropdownOption[]>(
						'securityRoles/dropdown',
						undefined
					),
					httpService.get<DropdownOption[]>(
						'divisions/dropdown',
						undefined
					)
				]);

				setDropdownsOptions({
					securityRolesOptions: securityRoles,
					divisionsOptions: divisions
				});
			} catch (error) {
				notifierService.error('Cannot load dropdowns options');
			}
		};

		fetchData();
	}, []);

	const resetDialog = (): void => {
		setUserData({
			firstName: '',
			lastName: '',
			userName: '',
			email: '',
			securityRoleId: undefined,
			defaultDivisionId: undefined,
			divisions: []
		});
		setIsVisible(false);
		setIsLoading(false);
	};

	const onActiveDivisionsChange = (e: MultiSelectChangeParams) => {
		const divisionsIds: number[] = e.target.value;

		const divisionsToRemain = divisions.filter((d) =>
			divisionsIds.includes(d.divisionId)
		);

		const newDivisionsIds = divisionsIds.filter(
			(dId) => !divisionsToRemain.some((x) => x.divisionId === dId)
		);

		const divisionsToAdd = divisionsOptions
			.filter((dOpt) => newDivisionsIds.includes(dOpt.value as number))
			.map((d) => {
				const divisionToAdd: IUserDivisionsUpdateRequestModel = {
					divisionId: d.value as number,
					receiveDivisionalNotifications: false
				};

				return divisionToAdd;
			});

		setUserData({
			...userData,
			divisions: divisionsToRemain.concat(divisionsToAdd)
		});
	};

	const onReceiveNotificationChange = (e: {
		value: number;
		checked: boolean;
	}) => {
		const divisionNotificationToUpdate = divisions.find(
			(d) => d.divisionId === e.value
		);

		if (!divisionNotificationToUpdate) {
			return;
		}

		divisionNotificationToUpdate.receiveDivisionalNotifications = e.checked;

		setUserData({
			...userData,
			divisions: divisions.map((item) =>
				item.divisionId === e.value
					? divisionNotificationToUpdate
					: item
			)
		});
	};

	const handleAddUser = async (): Promise<void> => {
		setIsLoading(true);

		try {
			await httpService.post(
				'users',
				JSON.stringify(userData),
				undefined,
				false
			);

			setShouldReload(true);
			resetDialog();
		} catch (err) {
			notifierService.error(err);
		} finally {
			setIsLoading(false);
		}
	};

	const addDialogFooter: JSX.Element = (
		<>
			<button
				className='btn primary'
				onClick={handleAddUser}
				disabled={
					!firstName ||
					!lastName ||
					!userName ||
					!email ||
					!securityRoleId ||
					!defaultDivisionId ||
					divisions.length <= 0
				}
			>
				Add
			</button>
			<button className='btn secondary' onClick={resetDialog}>
				Cancel
			</button>
		</>
	);

	const pendingFooter: JSX.Element = (
		<div className={styles.dialogFooter}>
			<p>Creating User...</p>
			<ProgressSpinner />
		</div>
	);

	return (
		<>
			<button
				className={`${styles.addButton} btn primary`}
				onClick={() => setIsVisible(true)}
			>
				Add User
			</button>

			<Dialog
				className={styles.dialog}
				header='Add User'
				visible={isVisible}
				onHide={resetDialog}
				footer={isLoading ? pendingFooter : addDialogFooter}
				baseZIndex={9999}
			>
				<div className={styles.dialogContent}>
					<label htmlFor='firstName'>First Name</label>
					<InputText
						id='firstName'
						placeholder='First Name'
						value={firstName}
						onChange={(e: ChangeEvent<HTMLInputElement>) => {
							setUserData({
								...userData,
								firstName: e.target.value
							});
						}}
					/>
				</div>

				<div className={styles.dialogContent}>
					<label htmlFor='lastName'>Last Name</label>
					<InputText
						id='lastName'
						placeholder='Last Name'
						value={lastName}
						onChange={(e: ChangeEvent<HTMLInputElement>) => {
							setUserData({
								...userData,
								lastName: e.target.value
							});
						}}
					/>
				</div>

				<div className={styles.dialogContent}>
					<label htmlFor='userName'>Username</label>
					<InputText
						id='userName'
						placeholder='Username'
						value={userName}
						onChange={(e: ChangeEvent<HTMLInputElement>) => {
							setUserData({
								...userData,
								userName: e.target.value
							});
						}}
					/>
				</div>

				<div className={styles.dialogContent}>
					<label htmlFor='email'>Email</label>
					<InputText
						id='email'
						placeholder='Email'
						value={email}
						type='email'
						onChange={(e: ChangeEvent<HTMLInputElement>) => {
							setUserData({
								...userData,
								email: e.target.value
							});
						}}
					/>
				</div>

				<div className={styles.dialogContent}>
					<label htmlFor='securityRole'>Security role</label>
					<Dropdown
						id='securityRole'
						value={securityRoleId}
						options={securityRolesOptions}
						onChange={(e) => {
							setUserData({
								...userData,
								securityRoleId: e.target.value
							});
						}}
					/>
				</div>

				<div className={styles.dialogContent}>
					<label htmlFor='defaultDivision'>Default division</label>
					<Dropdown
						id='defaultDivision'
						value={defaultDivisionId}
						options={divisionsOptions}
						onChange={(e) => {
							setUserData({
								...userData,
								defaultDivisionId: e.target.value
							});
						}}
					/>
				</div>

				<div className={styles.dialogContent}>
					<label htmlFor='activeDivisions'>Active divisions</label>
					<MultiSelect
						id='activeDivisions'
						placeholder='Select active divisions'
						value={activeDivisionsIds}
						options={divisionsOptions}
						optionLabel={nameof<DropdownOption>('label')}
						optionValue={nameof<DropdownOption>('value')}
						onChange={onActiveDivisionsChange}
					/>
				</div>

				{activeDivisionsIds.length > 0 ? (
					<div className={styles.dialogContent}>
						<label htmlFor='notifications'>
							Receive notifications
						</label>
						{activeDivisionsIds.map((divisionId) => {
							return (
								<div
									key={divisionId}
									className={styles.fieldCheckbox}
								>
									<Checkbox
										inputId={divisionId.toString()}
										value={divisionId}
										checked={
											divisions.find(
												(n) =>
													n.divisionId === divisionId
											)?.receiveDivisionalNotifications
										}
										onChange={onReceiveNotificationChange}
									/>
									<label htmlFor={divisionId.toString()}>
										{
											divisionsOptions.find(
												(d) => d.value === divisionId
											)?.label
										}
									</label>
								</div>
							);
						})}
					</div>
				) : (
					<br />
				)}
			</Dialog>
		</>
	);
};

export default AddUser;
